import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { storage } from "../helpers/storage";
import { openSnackbar } from "../../store/slices/snackbar";

/**
 * Prevents non-locus users from accessing client routes that don't belong to them.
 * Uses cookie fallback when Redux state hasn't loaded yet to avoid race conditions.
 */
const ClientGuard = ({ children }) => {
  const { clientId } = useParams();
  const { activeUser } = useSelector((state) => state.user);
  const { selectedClient } = useSelector((state) => state.customer);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Use cookie as fallback when Redux state hasn't loaded yet
  const isLocusUser = activeUser?.domain === "locus" || storage.getLocus();
  const resolvedClientId = selectedClient?.id || storage.getClientId();

  const isMismatch =
    clientId &&
    resolvedClientId &&
    String(clientId) !== String(resolvedClientId);

  useEffect(() => {
    if (!isLocusUser && isMismatch) {
      dispatch(
        openSnackbar({
          open: true,
          message: "You don't have access to that client. Redirecting to your account.",
          variant: "alert",
          alert: { color: "warning", variant: "filled" },
          close: true,
        }),
      );
      navigate(`/client/${resolvedClientId}/overview`, { replace: true });
    }
  }, [clientId, resolvedClientId, isLocusUser, isMismatch, navigate, dispatch]);

  // Block render while redirecting to prevent child components from making API calls
  if (!isLocusUser && isMismatch) {
    return null;
  }

  return children;
};

export default ClientGuard;
